import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    option?: ProductModel;
    currentPageUrl?: string;
    productIndex: number;
    kitText: boolean;
    title: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const path = location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    return {
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        currentPageUrl,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsKitSectionDetailsStyles {
    OptionText?: TypographyPresentationProps;
    Container?: GridContainerProps;
    GridItem?: GridItemProps;
}

export const productDetailsKitSectionOptionsStyles: ProductDetailsKitSectionDetailsStyles = {
    OptionText: {
        css: css`
            display: block;
        `,
    },
    Container: {
        gap: 5,
        css: css`
            a img {
                max-width: 8.063rem;
                height: auto;
            }
            > div:first-child {
                display: flex;
                align-items: center;
                @media (max-width: 767px) {
                    margin-top: 1rem;
                    margin-bottom: 1rem;
                }
            }
            div {
                a {
                    color: #002554;
                    span {
                        color: #002554 !important;
                    }
                    &:hover {
                        color: #005cb9 !important;
                        text-decoration: underline;
                    }
                }
                @media (max-width: 767px) {
                    width: 100%;
                    flex-basis: 100% !important;
                    max-width: 100% !important;
                    text-align: center;
                    margin: 0 auto;
                    a {
                        margin: 0 auto;
                    }
                }
            }
        `,
    },
    GridItem: {
        width: 12,
    },
};

const styles = productDetailsKitSectionOptionsStyles;

const ProductDetailsKitSectionDetails: React.FC<Props> = ({
    option,
    productIndex,
    currentPageUrl,
    category,
    kitText,
    title,
}) => {
    if (!option || option.productTitle.length === 0) {
        return null;
    }

    const productUrl = `/product/${option.urlSegment}`;
    const imgSource = option.smallImagePath;
    const image = <Img src={imgSource} altText={option.imageAltText} loading="lazy" height="129px" />;
    const history = useHistory();
    const position = productIndex + 1;
    const productClickDataLayerEvent = () => {
        let listName = "Product Details";
        listName = currentPageUrl.includes("Search")
            ? "Search Results"
            : currentPageUrl.includes("Cart")
                ? "Cart Lines"
                : currentPageUrl.includes("SavedOrders")
                    ? "Saved Order Details Page"
                    : currentPageUrl.includes("Orders/Details")
                        ? "Order History Details"
                        : currentPageUrl.includes("MyLists")
                            ? "Product Wish List"
                            : currentPageUrl.includes("CheckoutReviewAndSubmit")
                                ? "Order Review"
                                : currentPageUrl.includes("OrderConfirmation")
                                    ? "Order Confirmation"
                                    : listName;

        const dataLayer = (window as any).dataLayer || [];
        // dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        const tab = title ?? "";
        dataLayer.push({
            event: "productClick",
            ecommerce: {
                click: {
                    actionField: { list: listName, action: "click" }, // Optional list property.
                    products: [
                        {
                            name: option.productTitle, // Name or ID is required.
                            id: option.productNumber,
                            price: option.unitListPrice,
                            brand: option.brand?.name,
                            condition: option.properties.condition,
                            // category: category?.shortDescription ?? "",
                            // stock:
                            //     "product" in product
                            //         ? (
                            //             product.productInfo?.inventory?.inventoryAvailabilityDtos?.find(
                            //                 o =>
                            //                     o.unitOfMeasure.toLowerCase() ===
                            //                     (product.productInfo?.unitOfMeasure?.toLowerCase() || ""),
                            //             )?.availability || undefined
                            //         )?.message
                            //         : "",
                            // stockqty: "product" in product ? product.productInfo.inventory?.qtyOnHand : 0,
                            position,
                            tab,
                        },
                    ],
                },
            },
        });
        setTimeout(() => {
            history.push(productUrl);
        }, 500);
    };
    return (
        <GridContainer {...styles.Container}>
            <GridItem {...styles.GridItem}>
                {productUrl ? (
                    <GridContainer {...styles.Container}>
                        <GridItem width={[12, 12, 5, 5, 4]}>
                            <Link href={productUrl} onClick={productClickDataLayerEvent}>
                                {image}{" "}
                            </Link>
                        </GridItem>
                        {kitText ? (
                            <GridItem width={[12, 12, 7, 7, 8]}>
                                <Link href={productUrl} onClick={productClickDataLayerEvent}>
                                    {option.properties?.qtyneeded} {translate("of")} {option.productTitle}
                                </Link>
                            </GridItem>
                        ) : (
                            <GridItem width={[12, 12, 7, 7, 8]}>
                                <Link href={productUrl} onClick={productClickDataLayerEvent}>
                                    {option.productTitle}
                                </Link>
                            </GridItem>
                        )}
                    </GridContainer>
                ) : (
                    <GridContainer {...styles.Container}>
                        <GridItem width={[12, 12, 3, 3, 2]}>{image}</GridItem>
                        {kitText ? (
                            <GridItem width={[12, 12, 9, 9, 10]}>
                                {option.properties?.qtyneeded} {translate("of")} {option.productTitle}
                            </GridItem>
                        ) : (
                            <GridItem width={[12, 12, 9, 9, 10]}>{option.productTitle}</GridItem>
                        )}
                    </GridContainer>
                )}
            </GridItem>
        </GridContainer>
    );
};
export default connect(mapStateToProps)(ProductDetailsKitSectionDetails);
